import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from '@modifi/plugin-i18n'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {Col, DangerAlert, H5, Row, SuccessAlert, ButtonLink, PreviousIcon} from '@modifi/ui'
import {Form} from '@modifi/plugin-forms'

import {LegalRepresentativeFormData} from '../constants'
import LegalRepresentativesFormContent from '../components/legal-rep-form-content'
import SignatoryFieldWatcher from '../components/signatory-field-watcher'
import PersonFormFooter from '../../components/person-form-footer'
import {ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES} from '../../../../routes/constants'
import {FormContentWrapper} from '../../../../ui/form'
import {EntityFormData, SelectOption} from '../../../../types/common'

export type CreateLegalRepresentativeProps = {
  isLoading: boolean
  updatePending: boolean
  initialize: (externalCompanyId: string) => void
  externalCompanyId: string
  formData: EntityFormData<LegalRepresentativeFormData>
  onSubmit: (changes: Record<string, unknown>, externalCompanyId: string) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  dataSources: SelectOption[]
  onSelectDataSource: (value: string) => void
  pageIsVerified: boolean
  hasAnyRoleBasedFieldsVisible: boolean
  redirect: (route: string) => void
}

// without the utility bill upload the flow ends one page earlier
const getIsFinalStep = (
  currentPage: number,
  hasDataSources: boolean,
  shouldRenderUtilityBill: boolean
) => {
  if (!shouldRenderUtilityBill) {
    return hasDataSources ? currentPage === 2 : true
  }
  return hasDataSources ? currentPage === 3 : currentPage === 2
}

const CreateLegalRepresentative: React.FC<CreateLegalRepresentativeProps> = ({
  updatePending,
  externalCompanyId,
  hasError,
  errorMessage,
  isLoading,
  onSubmit,
  initialize,
  formData,
  onDiscard,
  dataSources,
  onSelectDataSource,
  pageIsVerified,
  hasAnyRoleBasedFieldsVisible,
  redirect,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [currentPage, setCurrentPage] = useState(1)
  const [shouldRenderUtilityBill, setShouldRenderUtilityBill] = useState(false)

  const hasDataSources = dataSources.length > 0
  const isFinalStep = getIsFinalStep(currentPage, hasDataSources, shouldRenderUtilityBill)

  useEffect(() => {
    initialize(externalCompanyId)
  }, [initialize, externalCompanyId])

  useEffect(() => {
    onDiscard()
  }, [onDiscard])

  // the last step submits, the remaining data source steps move on to the next page
  const submitLabel =
    isFinalStep || (currentPage === 2 && hasDataSources && !hasAnyRoleBasedFieldsVisible)
      ? (t('sub-section-footer.cta-submit') as string)
      : (t('sub-section-footer.cta-next') as string)

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      if (
        !isFinalStep &&
        (currentPage === 1 ||
          (currentPage === 2 && dataSources.length > 0 && hasAnyRoleBasedFieldsVisible))
      ) {
        setCurrentPage(currentPage + 1)
      } else {
        onSubmit(changes, externalCompanyId)
      }
    },
    [
      onSubmit,
      externalCompanyId,
      currentPage,
      dataSources.length,
      hasAnyRoleBasedFieldsVisible,
      isFinalStep,
    ]
  )

  const handleBackButtonClick = useCallback(() => {
    setCurrentPage(currentPage - 1)
  }, [currentPage])

  if (isLoading) {
    return null
  }
  return (
    <CustomerUploadManager>
      {pageIsVerified && (
        <SuccessAlert>{t('legal-representative-page.fully-verified-alert')}</SuccessAlert>
      )}
      {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}
      <Row>
        <Col>
          <ButtonLink
            data-target="legal-rep-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row>
        <Col mb={40}>
          <H5>{t('legal-representative-create-page.heading')}</H5>
        </Col>
      </Row>

      <Form
        isLoading={updatePending}
        // @ts-expect-error handling of null values
        defaultValues={formData.defaultValues}
        // @ts-expect-error handling of null values
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <FormContentWrapper>
          <LegalRepresentativesFormContent
            t={t}
            isNewLegalRepresentative
            selectPersonAsDataSource={onSelectDataSource}
            dataSourceOptions={dataSources}
            currentPage={currentPage}
            hasAnyRoleFieldItem={hasAnyRoleBasedFieldsVisible}
          />
        </FormContentWrapper>

        <SignatoryFieldWatcher onVisibilityChange={setShouldRenderUtilityBill} />

        <PersonFormFooter
          loading={updatePending}
          hasError={hasError}
          submitLabel={submitLabel}
          canMoveBack={currentPage !== 1}
          handleBackButtonClick={handleBackButtonClick}
          isFinalStep={isFinalStep}
        />
      </Form>
    </CustomerUploadManager>
  )
}

export default CreateLegalRepresentative
